import { useCallback, useEffect, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useCommands } from "@components/commands/useCommands";

interface Props {
    canBulkEnable: boolean;
    canBulkDisable: boolean;
    canBulkDelete: boolean;
    bulkArchiveBlockReason: string | undefined;
    bulkDeleteBlockReason: string | undefined;
    onBulkEnable: () => void;
    onBulkDisable: () => void;
    onBulkDelete: () => void;
}

// ⌘K is the command palette's own global hotkey; taking it over through the
// palette's override is what keeps both from opening at once.
export const NestedEditorBulkActionsMenu = ({
    canBulkEnable,
    canBulkDisable,
    canBulkDelete,
    bulkArchiveBlockReason,
    bulkDeleteBlockReason,
    onBulkEnable,
    onBulkDisable,
    onBulkDelete,
}: Props) => {
    const { registerOpenOverride, unregisterOpenOverride } = useCommands();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        registerOpenOverride(() => setIsOpen(true));
        return () => unregisterOpenOverride();
    }, [registerOpenOverride, unregisterOpenOverride]);

    const runBulkAction = useCallback((action: () => void) => {
        setIsOpen(false);
        action();
    }, []);

    const archiveTooltip =
        bulkArchiveBlockReason ?? "All selected accounts are already archived";

    return (
        <Menu.Root
            open={isOpen}
            onOpenChange={setIsOpen}
            align="end"
            trigger={
                <Button
                    variant="primary"
                    size="md"
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="mod+k" />
                        </span>
                    }
                >
                    Actions
                </Button>
            }
        >
            <Menu.Item
                disabled={!canBulkEnable}
                disabledReason="All selected accounts are already enabled"
                onClick={() => runBulkAction(onBulkEnable)}
            >
                Enable
            </Menu.Item>
            <Menu.Item
                disabled={!canBulkDisable}
                disabledReason={archiveTooltip}
                onClick={() => runBulkAction(onBulkDisable)}
            >
                Archive
            </Menu.Item>
            <Menu.Item
                destructive
                disabled={!canBulkDelete}
                disabledReason={bulkDeleteBlockReason}
                onClick={() => runBulkAction(onBulkDelete)}
            >
                Delete
            </Menu.Item>
        </Menu.Root>
    );
};
